<template>
  <el-row class="shrimp-talk-settings-pondlogs" v-loading="loading">
    <el-row v-if="loading || Ponds.length > 0">
      <el-alert
        ref="error_alert"
        v-if="unhandledErrorMessage !== ''"
        :title="unhandledErrorMessage"
        type="error"
      ></el-alert>

      <el-row class="st-settings-table">
        <layout-toolbar style="margin: 10px 10px;">
          <er-button
            size="mini"
            btnType="save"
            :showLabel="true"
            :showIcon="true"
            :disabled="isReadOnly"
            @click="submitUpdatedDetails"
          ></er-button>
          <er-button
            size="mini"
            btnType="cancel"
            :showLabel="true"
            :disabled="isReadOnly"
            @click="loadSTList"
          ></er-button>
          <er-column-selector
            :columns="tableColumns"
            keyToStoreDefaults="shrimp_talk_settings"
            :title="'Column Visibility'"
            @change-selection="handleChangeColumnSelection"
          ></er-column-selector>
        </layout-toolbar>
        <er-data-tables
          ref="tableST"
          uniqueId="shrimp-talk-settings"
          :merge="{ startColumnNumber: 1, endColumnNumber: 1 }"
          :columns="selectedHeaders"
          :key="$i18n.locale"
          :tableData="dataValues"
          :eltableProps="tableProps"
          :pagination-slot="true"
          :sortMethodForColumns="sortMethods()"
          type="white-header-table"
          :action-column="false"
        >
          <template v-slot:pond_id="{ row }">
            {{ getPondName(row.data.pond_id) }}
          </template>
          <template v-slot:v1_code="{ row }">{{ row.data.v1_title }}</template>

          <template v-slot:title="{ row }">
            <div class="alias-container">
              <el-input
                v-model="row.data.title"
                :placeholder="tableColumns.title.label"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
                @change="handlePMChangeInTable($event, row.data)"
                size="mini"
              ></el-input>
            </div>
          </template>

          <template v-slot:pond_mothers="{ row }">
            <div class="pm-contianer">
              <er-select
                v-model="row.data.pond_mothers"
                :placeholder="tableColumns.pond_mothers.label"
                :collapse-tags="false"
                multiple
                :multiple-limit="2"
                value-key="_id"
                size="mini"
                popper-class="st-dropdown"
                @visible-change="
                  handleFullChangeInPmSelection($event, row.data)
                "
                @change="handleChangeInPMSelection($event, row.data)"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
              >
                <li
                  class="el-select-dropdown__item"
                  :class="selectAllCheckConditions(row.data) ? `selected` : ''"
                  @click.stop="
                    handleSelectAll(
                      $event,
                      row.data,
                      Object.values(pondPmsData[row.data.pond_id])
                    )
                  "
                >
                  <span class="item-name">{{ $t("select_all") }}</span>
                  <span class="main-item-header">
                    <center>{{ $t("Comn_main") }}</center>
                  </span>
                  <!-- <el-checkbox @click="handleSelectAll"></el-checkbox> -->
                </li>
                <template v-for="pm in pondPmsData[row.data.pond_id]">
                  <template slot="default">
                    <el-option
                      v-if="pm"
                      :label="pm.code"
                      :key="pm.code + '' + row.data._id"
                      :value="pm"
                      :disabled="disablePmSelectOption(pm, row.data)"
                    >
                      <!-- pm.code -->
                      <span slot="default" class="item-name">{{
                        pm.code
                      }}</span>
                      <span class="main-item-radio">
                        <el-radio
                          :label="pm._id"
                          :key="`${pm.code}-radio`"
                          v-model="row.data.main_pond_mother_id"
                          @click.native.stop="handleRadioClick"
                          :disabled="
                            row.data.pond_mothers
                              .map(pm => pm._id)
                              .indexOf(pm._id) === -1
                          "
                          >{{ "" }}</el-radio
                        >
                      </span>
                    </el-option>
                  </template>
                </template>
              </er-select>
            </div>
            <!-- </el-radio-group> -->
          </template>

          <template v-slot:start_time="{ row }">
            <el-row class="time-field">
              <el-time-picker
                v-model="row.data.settings.start_time"
                size="mini"
                :range-separator="$t('Comn_to')"
                value-format="HH:mm"
                format="HH:mm"
                :picker-options="pickerOptions"
                :placeholder="tableColumns.start_time.label"
                @change="handleChangeTimeRange($event, row.data, 'start_time')"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
                :clearable="false"
              ></el-time-picker>
            </el-row>
          </template>

          <template v-slot:end_time="{ row }">
            <el-row class="time-field">
              <el-time-picker
                v-model="row.data.settings.end_time"
                size="mini"
                value-format="HH:mm"
                format="HH:mm"
                :picker-options="pickerOptions"
                :placeholder="tableColumns.end_time.label"
                @change="handleChangeTimeRange($event, row.data, 'end_time')"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
                :clearable="false"
              ></el-time-picker>
            </el-row>
          </template>

          <template v-slot:pause_start="{ row }">
            <el-row class="time-field">
              <el-time-picker
                v-model="row.data.settings.pause_start"
                size="mini"
                :range-separator="$t('Comn_to')"
                value-format="HH:mm"
                format="HH:mm"
                :picker-options="pickerOptions"
                :placeholder="tableColumns.pause_start.label"
                @change="handleChangeTimeRange($event, row.data, 'pause_start')"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
                :clearable="false"
              />
            </el-row>
          </template>

          <template v-slot:pause_end="{ row }">
            <el-row class="time-field">
              <el-time-picker
                v-model="row.data.settings.pause_end"
                size="mini"
                :range-separator="$t('Comn_to')"
                value-format="HH:mm"
                format="HH:mm"
                :picker-options="pickerOptions"
                :placeholder="tableColumns.pause_end.label"
                @change="handleChangeTimeRange($event, row.data, 'pause_end')"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
                :clearable="false"
              />
            </el-row>
          </template>
          <template v-slot:wifi_signal_RSSI="{ row }">
            <p>
              {{
                row.data.sensor_values
                  ? row.data.sensor_values.wifi_signal_RSSI
                  : ""
              }}
            </p>
          </template>
          <template v-slot:wifi_connection="{ row }">
            <p>
              {{
                row.data.sensor_values
                  ? row.data.sensor_values.wifi_connection
                  : ""
              }}
            </p>

            <p></p
          ></template>

          <template v-slot:feed_limit="{ row }">
            <div class="table-feed-limit">
              <el-input-number
                v-model="row.data.settings.feed_limit"
                :placeholder="tableColumns.feed_limit.label"
                size="mini"
                :controls="false"
                @change="handleChangeInFeedLimit($event, row.data)"
                :disabled="isSTExistInSelectedSTs(row.data._id)"
              ></el-input-number>
            </div>
          </template>
          <template v-slot:feeding_level="{ row }">
            <er-select
              v-model="row.data.settings.feeding_level"
              value-key="_id"
              :placeholder="$t('Ponds_select_pond_name')"
              filterable
              collapse-tags
              @change="handleChangePond"
              :no-data-text="$t('Comn_no_data_connection')"
              size="mini"
              popper-class="pm-dropdown"
            >
              <template v-for="level in feedingLevel">
                <el-option
                  :key="level.value"
                  :label="level.label"
                  :value="level.value"
                ></el-option>
              </template>
            </er-select>
          </template>
        </er-data-tables>
      </el-row>
    </el-row>
    <er-no-ponds :tabData="tabData" :disabled="isReadOnly" v-else />
  </el-row>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin.js";
import PondMother from "@/model/pondmother";
import ShrimpTalk from "@/model/shrimptalk";

export default {
  mixins: [errorHandlerMixin],
  props: {
    tabData: {
      default: {}
    }
  },
  data: function() {
    return {
      data: {},
      total: 0,
      mainPM: "",
      isPondUnAssign: false,
      pondMotherObj: new PondMother(),
      shrimpTalkObj: new ShrimpTalk(),
      pondsSelected: [],
      errMessagesObject: new PondMother(),
      selectedPMIds: [],
      editPMObj: {},
      tableEditSTObj: {},
      pondPmsData: {},
      selectAllChecked: {},
      selectedHeaders: {},
      selectedSTIds: [],
      pickerOptions: {
        format: "HH:mm"
      },
      loading: false,
      tableProps: {
        // border: true,
        size: "small",
        stripe: true,
        rowKey: this.getRowKey,
        defaultSort: {
          prop: "pond_id",
          order: "ascending"
        }
      },
      disabled: false,
      query: {
        get_all: true,
        order_by: null,
        order_type: null,
        un_assigned_only: false,
        location_id: null
      },
      editStObj: {},
      feedingLevel: [
        {
          label: "Normal",
          value: 0
        },
        {
          label: "Economical",
          value: 1
        },
        {
          label: "Aggressive",
          value: 2
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      PondMothers: "pondmother/getPondMothers",
      mapPondIdPond: "pond/getMapPondidPond",
      mapStIdSt: "shrimptalk/getMapStidSt",
      Ponds: "pond/getPonds",
      getPmIdPm: "pondmother/getPmIdPm",
      getMapPmIdMode: "pondmother/getMapPmIdMode",
      ShrimpTalks: "shrimptalk/getShrimpTalks",
      getUserTimeZoneOffset: "user/getUserTimeZoneOffset"
    }),
    ...mapGetters("user", {
      isReadOnly: "isReadOnly"
    }),
    ...mapGetters("pond", {
      Ponds: "getPonds"
    }),
    stPonds() {
      return new Set(this.dataValues.map(({ pond_id }) => pond_id));
    },
    disablePmSelectOption() {
      return (pondmother, shrimptalk) => {
        const conditions = [
          pondmother.shrimp_talk_id !== null &&
            pondmother.shrimp_talk_id !== shrimptalk._id
        ];
        return conditions.every(x => x);
      };
    },
    selectAllCheckConditions() {
      return function(st) {
        const conditions = [
          this.selectAllChecked[st._id],
          Object.keys(this.pondPmsData[st.pond_id] || []).length ===
            st.pond_mothers.length,
          st.pond_mothers.length === 2
        ];
        return conditions.some(val => val === true);
      };
    },
    getSelectedPondST() {
      return this.dataValues.filter(({ pond_id }) =>
        this.pondsSelected.includes(pond_id)
      );
    },
    selectedPondPMs: function() {
      return this.pondsSelected
        .map(pond => this.mapPondIdPond.get(pond).pond_mothers)
        .flat(1);
    },
    selectedPondPMIds: function() {
      return this.selectedPondPMs.map(pm => pm._id);
    },
    pondModesOfOperations() {
      return {
        basic: "PM_sch_mode4",
        schedule: "PM_schedule",
        automatic: "PM_automatic"
      };
    },
    tableColumns() {
      return {
        pond_id: {
          prop: "pond_id",
          label: this.$t("Comn_pond_name"),
          // width: "180",f
          fixed: true,
          minWidth: "140px",
          default: true,
          sortable: "custom"
        },
        v1_code: {
          prop: "v1_code",
          label: this.$t("Shrimptalk_id"),
          default: true,
          sortable: true,
          minWidth: "120px"
          // width: 180
        },
        ip_address: {
          prop: "settings.ip_address",
          label: this.$t("Comn_ip_addr"),
          default: false,
          width: "100px"
        },
        mac_address: {
          prop: "settings.mac_address",
          label: this.$t("Comn_mac_address"),
          default: false,
          width: "100px"
        },
        wifi_connection: {
          prop: "sensor_values.wifi_connection",
          label: "WiFi SSID",
          default: false,
          minWidth: "120px"
        },
        wifi_signal_RSSI: {
          prop: "sensor_values.wifi_signal_RSSI",
          label: this.$t("wifi_signal"),
          default: false,
          minWidth: "120px"
        },
        pond_mothers: {
          prop: "pond_mothers",
          label: this.$t("PM_pondmothers"),
          minWidth: "200px",
          default: true
        },
        start_time: {
          prop: "settings.start_time",
          label: this.$t("PM_start_time"),
          default: true,
          minWidth: "110px"
        },
        end_time: {
          prop: "settings.end_time",
          label: this.$t("PM_end_time"),
          default: true,
          minWidth: "110px"
        },
        pause_start: {
          prop: "settings.pause_start",
          label: this.$t("PM_pause_start"),
          default: false,
          minWidth: "110px"
        },
        pause_end: {
          prop: "settings.pause_end",
          label: this.$t("PM_pause_end"),
          default: false,
          minWidth: "110px"
        },
        feed_limit: {
          prop: "settings.feed_limit",
          label: this.$t("PM_day_feed"),
          sub_label: "(kg)",
          default: true,
          minWidth: "110px"
        },
        feeding_level: {
          prop: "feeding_level",
          label: this.$t("Comn_feeding_level"),
          default: false,
          minWidth: "140px"
        },
        title: {
          prop: "title",
          label: this.$t("Comn_alias_name"),
          default: true,
          minWidth: "150px"
        }
      };
    },
    dataValues() {
      const tableData = Object.values(this.data).sort((x, y) => {
        return this.$commonUtils.alphaNumericComparator(
          this.mapPondIdPond.get(x.pond_id).title,
          this.mapPondIdPond.get(y.pond_id).title
        );
      });

      return tableData;
    },
    isTableEditted() {
      return Object.keys(this.tableEditSTObj).length > 0;
    },
    location() {
      return this.$store.getters["user/getCurrUserLocation"];
    }
  },

  mounted() {
    this.initComponent();
  },

  methods: {
    ...mapActions({
      fetchAllPondMothers: "pondmother/fetchAllPondMothers",
      fetchAllShrimpTalks: "shrimptalk/fetchAllShrimpTalks",
      fetchAllPonds: "pond/fetchAllPonds",
      updateShrimpTalkDetails: "shrimptalk/updateShrimpTalkDetails",
      addPondMotherToShrimpTalk: "shrimptalk/addPondMotherToShrimpTalk",
      removePondMotherFromShrimpTalk:
        "shrimptalk/removePondMotherFromShrimpTalk"
    }),
    initComponent() {
      this.pondsSelected = [];
      this.selectedPMIds = [];
      this.pondMotherObj = new PondMother();
      this.loadSTList();
    },
    loadSTList: async function() {
      this.loading = true;
      try {
        this.query.status = ["ACTIVE"];
        await Promise.all([
          this.fetchAllPonds({
            location_id: this.location._id,
            status: ["ACTIVE", "INACTIVE"],
            exclude: ["geometry"],
            get_all: true
          }),
          this.fetchAllPondMothers({
            location_id: this.location._id,
            un_assigned_only: false,
            get_all: true
          })
        ]);
        this.Ponds.forEach(pond => {
          this.pondPmsData[pond._id] = {};
          pond.pond_mothers.forEach(pm => {
            this.pondPmsData[pond._id][pm._id] = this.$lodash.cloneDeep(
              this.getPmIdPm.get(pm._id)
            );
          });
        });
        const response = await this.fetchAllShrimpTalks({
          location_id: this.location._id,
          un_assigned_only: false,
          get_all: true
        });
        this.total = response.totalSTs;

        const clonedSts = this.$lodash.cloneDeep(this.ShrimpTalks);

        clonedSts.forEach(st => {
          this.$set(this.data, st._id, st);
        });
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    isSTExistInSelectedSTs(stId) {
      return this.selectedSTIds.indexOf(stId) > -1;
    },
    handleChangePond(pondIdArr) {
      this.handleCheckAllSTs(true);
      // if (pondIdArr.length > 0) {
      //   // this.pondMotherObj = new PondMother();
      //   this.shrimpTalkObj = new ShrimpTalk();
      //   // this.handleCheckAllPMs(true);
      //   Object.keys(this.tableEditSTObj).forEach(stId => {
      //     this.$set(
      //       this.data,
      //       pmId,
      //       this.$lodash.cloneDeep(this.getPmIdPm.get(pmId))
      //     );
      //   });
      //   this.tableEditSTObj = {};
      // } else {
      //   // this.handleCheckAllPMs(false);
      // }
    },
    handleChangeShrimpTalkChange(stIdArr) {
      // if (pmIdArr.length === 0) {
      //   this.handleCheckAllPMs(false);
      // } else {
      //   this.shrimpTalkObj = new ShrimpTalk();
      //   this.initEditPMObj(pmIdArr, this.shrimpTalkObj);
      // }
    },
    handleCheckAllSTs(value) {
      this.selectedSTIds = value
        ? this.getSelectedPondST.map(({ _id }) => _id)
        : [];
      // this.initEditPMObj(this.selectedPMIds, this.pondMotherObj);
      // this.isIndeterminatePMs = false;
    },
    getPondName(pondId) {
      if (pondId !== "") {
        return this.$lodash.capitalize(
          (this.mapPondIdPond.get(pondId) || { title: "Not Found" }).title
        );
      }
    },
    sortMethods() {
      return {
        pond_id: this.sortPondWise
      };
    },
    sortPondWise(a, b) {
      return this.$commonUtils.alphaNumericComparator(
        this.getPondName(a),
        this.getPondName(b)
      );
    },
    getRowKey(row) {
      return row.pond_mother_id;
    },
    getPond(pond_id) {
      if (this.mapPondIdPond && this.mapPondIdPond.get(pond_id)) {
        return this.mapPondIdPond.get(pond_id);
      } else {
        return { pond_mothers: [] };
      }
    },
    handlePMChangeInTable(event, pm) {
      if (this.editStObj[pm._id] == null) {
        this.$set(this.editStObj, pm._id, pm);
      }
    },
    handleChangeInPMSelection(selectedPms, st) {
      const selectedPmIds = selectedPms.map(pm => pm._id);
      const pondMothersObj = this.pondPmsData[st.pond_id];
      if (selectedPms.length > 0) {
        st.main_pond_mother_id = selectedPms[0]._id;
      }
      Object.values(pondMothersObj).forEach(pm => {
        if (selectedPmIds.indexOf(pm._id) > -1) {
          this.$set(pm, "shrimp_talk_id", st._id);
        } else if (pm.shrimp_talk_id === st._id) {
          this.$set(pm, "shrimp_talk_id", null);
        }
      });
      this.pondPmsData = Object.assign({}, this.pondPmsData);
      this.editStObj[st._id] = st;
      if (st.pond_mothers.length === 2) {
        this.selectAllChecked[st._id] = true;
      } else {
        this.selectAllChecked[st._id] = false;
      }
    },
    getRemovedPms(oldData, newData) {
      console.log(oldData, newData);
      const a = new Set(oldData);
      const b = new Set(newData);
      const difference = new Set([...a].filter(x => !b.has(x)));
      console.log(difference);
      return difference;
    },
    getAddedPms(oldData, newData) {
      console.log(oldData, newData);
      const a = new Set(oldData);
      const b = new Set(newData);
      const difference = new Set([...b].filter(x => !a.has(x)));
      console.log(difference);
      return difference;
    },
    async handleFullChangeInPmSelection(isOpened, st) {
      console.log(isOpened, st, "Blur struck");
      const selectedSt = this.ShrimpTalks.find(({ _id }) => _id === st._id);
      const oldPms = selectedSt.pond_mothers.map(({ _id }) => _id);
      const newPms = st.pond_mothers.map(({ _id }) => _id);
      console.log(newPms);
      console.log(oldPms);
      const removedPms = this.getRemovedPms(oldPms, newPms);
      const addedPms = this.getAddedPms(oldPms, newPms);
      try {
        console.log(removedPms);
        if (
          (!isOpened && addedPms.size > 0) ||
          (!isOpened && removedPms.size > 0)
        ) {
          this.loading = true;
          if (!isOpened && removedPms.size > 0) {
            const payload = {
              query: selectedSt._id,
              params: { pond_mothers: [...removedPms] }
            };
            await this.removePondMotherFromShrimpTalk(payload);
          }
          if (!isOpened && addedPms.size > 0) {
            const payload = {
              query: selectedSt._id,
              params: { pond_mothers: [...addedPms] }
            };
            await this.addPondMotherToShrimpTalk(payload);
          }
          this.$notify({
            title: this.$t("Usrs_success_msg"),
            message: this.$t("Shrimp_talk_details_updated_sucess"),
            duration: 5000,
            type: "success"
          });
          this.initComponent();
        }
      } catch (err) {
        this.loading = false;
        if (err.response && err.response.data.message) {
          errors.push({ message: err.response.data.message });

          this.errorFailToSave(errors);
        } else if (err.response && err.response.data.errors != null) {
          err.response.data.errors.details.forEach((el, index) => {
            errors.push({ message: el.message });
          });
          this.errorFailToSave(errors);
        } else {
          this.errorMessages(err, true);
        }
      }

      console.log(removedPms, addedPms);
    },
    handleChangeTimeRange(changedTimeRange, st, field_name) {
      st.settings[field_name] = changedTimeRange;
      this.editStObj[st._id] = st;
    },
    handleChangeInFeedLimit(nValue, st) {
      this.editStObj[st._id] = st;
    },
    handleRadioClick(event) {
      // console.log(event);
      this.$emit("input", event);
    },
    handleChangeColumnSelection(selectedColumns) {
      const columnObj = selectedColumns.reduce((acc, curr) => {
        acc[curr] = this.tableColumns[curr];
        return acc;
      }, {});
      this.selectedHeaders = Object.assign({}, columnObj);
    },
    handleSelectAll(event, st, listPMs) {
      this.selectAllChecked[st._id] = !this.selectAllChecked[st._id];
      if (this.selectAllChecked[st._id]) {
        st.pond_mothers = [];
        st.pond_mothers = listPMs
          .filter(
            pm => pm.shrimp_talk_id == null || pm.shrimp_talk_id === st._id
          )
          .slice(0, 2);
        st.pond_mothers.forEach(pm => {
          this.$set(pm, "shrimp_talk_id", st._id);
        });
        st.main_pond_mother_id = st.pond_mothers[0]._id;
      } else {
        st.pond_mothers.forEach(pm => {
          this.$set(pm, "shrimp_talk_id", null);
        });
        st.pond_mothers = [];
      }
      this.editStObj[st._id] = st;
    },

    validatePayload(datum, startTimeSecs, endTimeSecs, inSTTitle) {
      const errors = [];
      console.log(datum.pause_start, datum.pause_end);
      if (endTimeSecs <= startTimeSecs) {
        errors.push({
          message: `${this.$t(
            "PM_stop_time_must_greater_start_time"
          )} ${inSTTitle}`
        });
      } else if (datum.pause_start || datum.pause_end) {
        console.log(datum.pause_start, datum.pause_end);

        const pauseStartTimeSecs = this.$commonUtils.timeStrHHmmVal(
          datum.pause_start
        );
        const pauseEndTimeSecs = this.$commonUtils.timeStrHHmmVal(
          datum.pause_end
        );

        if (pauseStartTimeSecs <= startTimeSecs) {
          errors.push({
            message: `${this.$t(
              "PM_pause_stop_time_must_greater_start_time"
            )} ${inSTTitle}`
          });
        } else if (pauseEndTimeSecs >= endTimeSecs) {
          errors.push({
            message: `${this.$t(
              "PM_pause_end_time_must_smaller_end_time"
            )} ${inSTTitle}`
          });
        } else if (pauseEndTimeSecs <= pauseStartTimeSecs) {
          errors.push({
            message: `${this.$t(
              "PM_pause_end_time_must_greater_pause_start_time"
            )} ${inSTTitle}`
          });
        }
      }

      if (datum.feed_limit < 20) {
        errors.push({
          message: `${this.$tc("Comn_must_greater_than_or_equal", 2, {
            field: this.tableColumns.feed_limit.label,
            threshold: 20
          })} ${inSTTitle}`
        });
      }
      if (datum.feed_limit > 200) {
        errors.push({
          message: `${this.$tc("Comn_must_less_than_or_equal", 2, {
            field: this.tableColumns.feed_limit.label,
            threshold: 200
          })} ${inSTTitle}`
        });
      }
      return errors;
    },
    async submitUpdatedDetails() {
      let errors = [];
      try {
        this.isEditable(this.tabData);
        this.unhandledErrorMessageV2 = "";
        this.loading = true;
        const payLoadObj = {};

        const selectedSTs = this.getSelectedPondST
          .filter(({ _id }) => this.selectedSTIds.includes(_id))
          .map(st => JSON.parse(JSON.stringify(st)));
        if (selectedSTs.length > 0) {
          selectedSTs.forEach(
            st => (st.settings = this.shrimpTalkObj.settings)
          );
          selectedSTs.forEach(st => {
            this.editStObj[st._id] = st;
          });
        }

        Object.values(this.editStObj).forEach(st => {
          if (st != null && typeof st !== "undefined") {
            st.title = st.title || st.code;
            st.status = "ACTIVE";
            const pms = st.pond_mothers
              .filter(({ _id }) => !!_id)
              .map(({ _id, code }) => {
                return { _id, code, is_master: st.main_pond_mother_id === _id };
              });

            if (st.settings.pause_start || st.settings.pause_end) {
              if (!st.settings.pause_start) {
                throw {
                  type: "FAIL_TO_SAVE",
                  errors: [
                    {
                      message: `${this.$t("PM_pause_start")} ${this.$t(
                        "Comn_is_required"
                      )} ${st.title}`
                    }
                  ]
                };
              } else if (!st.settings.pause_end) {
                throw {
                  type: "FAIL_TO_SAVE",
                  errors: [
                    {
                      message: `${this.$t("PM_pause_end")} ${this.$t(
                        "Comn_is_required"
                      )} ${this.$t("Comn_in")} ${st.title}`
                    }
                  ]
                };
              }
              payLoadObj[st._id] = {
                pond_mothers: pms,
                title: st.title || st.code,
                shrimp_talk_id: st._id,
                start_time: st.settings.start_time.padEnd(8, ":00"),
                end_time: st.settings.end_time.padEnd(8, ":00"),
                pause_start: st.settings.pause_start.padEnd(8, ":00"),
                pause_end: st.settings.pause_end.padEnd(8, ":00"),
                feed_limit: st.settings.feed_limit,
                feeding_level: st.settings.feeding_level
              };
            } else {
              payLoadObj[st._id] = {
                pond_mothers: pms,
                title: st.title || st.code,
                shrimp_talk_id: st._id,
                start_time: st.settings.start_time.padEnd(8, ":00"),
                end_time: st.settings.end_time.padEnd(8, ":00"),
                feed_limit: st.settings.feed_limit,
                feeding_level: st.settings.feeding_level
              };
            }
          }
        });
        const payload = Object.values(payLoadObj);
        this.errorMessagesObject = new PondMother();

        if (payload.length === 0) {
          throw {
            type: "FAIL_TO_SAVE",
            errors: [
              {
                message: this.$t("Comn_no_changes_found", {
                  item: this.$t("Comn_shrimptalk")
                })
              }
            ]
          };
        }

        payload.forEach(datum => {
          const startTimeSecs = this.$commonUtils.timeStrHHmmVal(
            datum.start_time
          );
          const endTimeSecs = this.$commonUtils.timeStrHHmmVal(datum.end_time);

          const inSTTitle = `${this.$t("Comn_in")} ${datum.title}`;
          errors = [
            ...errors,
            ...this.validatePayload(
              datum,
              startTimeSecs,
              endTimeSecs,
              inSTTitle
            )
          ];
        });
        if (errors.length > 0) {
          throw {
            type: "FAIL_TO_SAVE",
            errors
          };
        }
        if (payload.length > 0) {
          await this.updateShrimpTalkDetails(payload);
          this.errorMessagesObject = new PondMother();
          this.$notify({
            title: this.$t("Usrs_success_msg"),
            message: this.$t("Shrimp_talk_details_updated_sucess"),
            duration: 5000,
            type: "success"
          });
          this.initComponent();
        }
      } catch (err) {
        console.log(err);
        if (err.response && err.response.data.message) {
          errors.push({ message: err.response.data.message });

          this.errorFailToSave(errors);
        } else if (err.response && err.response.data.errors != null) {
          err.response.data.errors.details.forEach((el, index) => {
            errors.push({ message: el.message });
          });
          this.errorFailToSave(errors);
        } else {
          this.errorMessages(err, true);
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.st-dropdown {
  .el-select-dropdown__item {
    // font-size: 11px;
    @include responsiveProperty(
      font-size,
      $app_font_size_small,
      $app_font_size_1,
      $app_font_size_2
    );
    // height: 20px;
    display: flex;
    align-items: center;
  }
  .item-name {
    min-width: 50px;
  }
  .main-item-radio,
  .main-item-header {
    margin-left: auto;
  }
}
.cancel-btn {
  cursor: pointer;
  background: rgba(229, 236, 243, 0.5);
  border: 1px solid #acc9f4;
  box-sizing: border-box;
  border-radius: 4px;
  width: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
}

.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.post-header-shrimp-talk {
  padding: 10px;
  width: 100%;
  display: flex;
  // justify-content: space-evenly;

  .ponds-container {
    width: 120px;
  }
  .st-container {
    width: 130px;
  }
}
.st-dropdown .el-select-dropdown__item {
  display: table;
  width: 100%;
  span.item-name {
    text-overflow: ellipsis;
    display: table-cell;
    width: 110px;
  }
  span.main-item-header {
    display: table-cell;
    right: 10px;
  }
  span.main-item-radio {
    display: table-cell;
    text-align: center;
    right: 10px;
  }
  .el-radio__input.is-checked .el-radio__inner::after {
    -webkit-transform: translate(-45%, -50%) scale(1.3);
    transform: translate(-45%, -50%) scale(1.3);
    top: 50%;
  }
  .el-radio__input.is-checked .el-radio__inner {
    border-color: #0a2463;
    background: #0a2463;
  }
  &.selected:after {
    content: "" !important;
  }
  &.selected:before {
    position: absolute;
    font-family: element-icons;
    content: "\E6DA";
    font-size: 12px;
    font-weight: 700;
    -webkit-font-smoothing: antialiased;
    left: 5px;
  }
}
.shrimp-talk-settings-pondlogs {
  $input_width: 100%;
  .st-settings-table {
    padding: 0 12px;
  }
  .filters {
    display: flex;
    flex-direction: row;
    justify-content: center;
    & > div:nth-child(n + 1) {
      margin-right: 10px;
    }
  }
  .el-input-number {
    width: 100%;
  }
  .el-select__tags {
    max-width: 170px !important;
  }
  .table {
    margin: 10px;
  }
  .el-date-editor.el-input,
  .el-date-editor.el-input__inner {
    width: 100%;
  }
  .alias-container {
    .el-input--mini {
      // @include responsiveProperty(width, 100px, 100px, 120px);
    }
  }
  .el-pagination__sizes {
    .el-input--mini .el-input__inner {
      height: 22px;
      line-height: 22px;
    }
  }
  .el-select__tags-text {
    color: #0a2463;
    background-color: #f2f0f9;
  }
  .el-select .el-tag.el-tag--info .el-tag__close {
    color: #909399;
    pointer-events: none;
    display: none;
  }

  .pm-tag-list {
    display: flex;
    flex-direction: row;
    margin: 10px;
    box-sizing: border-box;
  }
  .pagination-bar {
    margin-bottom: 10px;
  }
  .st-settings-table {
    .el-table {
      overflow: auto;
    }
    .pm-contianer {
      .el-select {
        @include responsiveProperty(width, 160px, 160px, 200px);
      }
    }
    .time-field {
      .el-input {
        @include responsiveProperty(width, 90px, 90px, 110px);
      }
    }
    .table-feed-limit {
      .el-input {
        width: 90px;
      }
    }
    .alias-container {
      .el-input {
        width: 140px;
      }
    }
  }
}
</style>